import getSensorReadingInterval from "./get-sensor-reading-interval";
import formatInterval, { formatIntervalShort, formatIntervalMedium } from "./format-interval";

/**
 * How often a sensor takes a reading vs. how often those readings reach us.
 *
 * Normally the two are the same: the sensor wakes every `send_interval`,
 * takes a reading and uploads it. With battery saver (log) mode on it still
 * takes a reading every `send_interval` but only uploads the batch every
 * `log_secs`, so the readings arrive in bursts (alerts still send instantly).
 *
 * @param sensor
 * @returns {{readingSecs: number|null, uploadSecs: number|null, usesLogMode: boolean}}
 */
export const getReadingFrequency = sensor => {

  const sendInterval = parseInt(sensor?.send_interval, 10);

  const readingSecs = Number.isFinite(sendInterval) && sendInterval > 0 ? sendInterval : null;
  const uploadSecs = getSensorReadingInterval(sensor);

  return {
    readingSecs,
    uploadSecs,
    // only counts as log mode when it actually batches uploads
    usesLogMode: uploadSecs !== null && uploadSecs !== readingSecs,
  };

};

/**
 * The pieces shown for a sensor's frequency, either of which may be empty:
 *
 *   reading -> how often a reading is taken
 *   log     -> how often battery saver mode uploads the log
 *              (empty unless log mode is batching uploads)
 *
 * A sensor that uploads every reading has room for "1 hour" / "30 mins";
 * one in log mode shows the pair in short form, "1 hr" / "48 hr".
 *
 * @param sensor
 * @returns {{reading: string, log: string}}
 */
export const readingFrequencyLabels = sensor => {

  const { readingSecs, uploadSecs, usesLogMode } = getReadingFrequency(sensor);

  if(usesLogMode) {
    return {
      reading: readingSecs === null ? '' : formatIntervalShort(readingSecs),
      log: formatIntervalShort(uploadSecs),
    };
  }

  return {
    reading: readingSecs === null ? '' : formatIntervalMedium(readingSecs),
    log: '',
  };

};

/**
 * Plain text frequency for a sensor: reading interval, then the log upload
 * interval when battery saver mode batches the uploads.
 *
 *   "1 hour"        reads and uploads every hour
 *   "30 mins"       reads and uploads every 30 minutes
 *   "1 hr / 48 hr"  reads every hour, battery saver uploads the log every 48 hours
 *
 * @param sensor
 * @param fallback returned when the sensor has no usable interval
 * @returns {string}
 */
export const formatReadingFrequency = (sensor, fallback = '—') => {

  const { reading, log } = readingFrequencyLabels(sensor);

  return [reading, log].filter(Boolean).join(' / ') || fallback;

};

/**
 * Full sentence version of the frequency, for tooltips.
 *
 * @param sensor
 * @returns {string}
 */
export const describeReadingFrequency = sensor => {

  const { readingSecs, uploadSecs, usesLogMode } = getReadingFrequency(sensor);

  if(readingSecs === null && uploadSecs === null) {
    return 'No reading interval set';
  }

  if(usesLogMode) {
    const reads = readingSecs === null ? '' : `Takes a reading every ${formatInterval(readingSecs)}. `;
    return `${reads}Battery saver mode uploads the log every ${formatInterval(uploadSecs)} (alerts still send instantly)`;
  }

  return `Takes and uploads a reading every ${formatInterval(readingSecs)}`;

};

/**
 * Sort key for the frequency column: reading interval first, then the
 * upload interval to break ties. Sensors with no interval sort first.
 *
 * @param sensor
 * @returns {number}
 */
export const readingFrequencySortValue = sensor => {

  const { readingSecs, uploadSecs } = getReadingFrequency(sensor);

  if(readingSecs === null && uploadSecs === null) {
    return -1;
  }

  // both intervals are well under 1e6 seconds, so this keeps them separable
  return (readingSecs ?? 0) * 1e6 + (uploadSecs ?? 0);

};
